// ============================================================================
// Pure logic of the gate scan mode (/media/scan) and of the context-aware
// /media-verify card.
//
// Both read the `scan-accreditation` response. Contract v2 (see
// docs/superpowers/plans/2026-10-04-media-qr-scan-experience.md) only ADDS
// fields — recorded_as, timing, match, valid_from / valid_until,
// previous_entry, accreditation.qr_kind / holder_name_ar — so every function
// here must give the v1 answer when they are absent: the server is deployed
// independently of this webapp.
//
// Node-testable: no React, no i18n, no Supabase, no DOM (storage is injected).
// ============================================================================

import type {
    ScanAccreditationResponse,
    ScanMatchSummary,
    ScanQrKind,
    ScanTiming,
} from "@/types/media";
import type { DisplayMode } from "@/utils/nameHelpers";
import { verifyErrorKey } from "./scanLogic";

// ------------------------------------------------------------ QR content ----

/** A raw token as minted by generate-accreditation-token (a UUID today). */
const RAW_TOKEN_RE = /^[A-Za-z0-9_-]{16,128}$/;

function tokenFromParams(query: string): string | null {
    const value = new URLSearchParams(query).get("token")?.trim() ?? "";
    return RAW_TOKEN_RE.test(value) ? value : null;
}

/**
 * The token carried by a scanned QR code, or null when the code is not an
 * accreditation QR (a ticket, a URL without token, free text…).
 *
 * Accepted: the verification URL printed on badges and shown by the media app
 * (`https://moubarati.com/media-verify?token=…`, any host, also with a hash
 * route `#/media-verify?token=…` or the legacy `/verify?token=…`), or the bare
 * token itself.
 */
export function parseScannedToken(raw: string | null | undefined): string | null {
    const value = (raw ?? "").trim();
    if (!value) return null;
    if (RAW_TOKEN_RE.test(value)) return value;

    let url: URL | null = null;
    try {
        url = new URL(value);
    } catch {
        url = null;
    }
    if (url) {
        const fromSearch = tokenFromParams(url.search);
        if (fromSearch) return fromSearch;
        const hash = url.hash ?? "";
        const q = hash.indexOf("?");
        return q >= 0 ? tokenFromParams(hash.slice(q + 1)) : null;
    }
    // Scheme-less "moubarati.com/media-verify?token=…" (some QR apps strip it).
    const q = value.indexOf("?");
    return q >= 0 ? tokenFromParams(value.slice(q + 1)) : null;
}

// -------------------------------------------------------------- debounce ----

export const SCAN_DEBOUNCE_MS = 4_000;

/**
 * Drops a token seen again within `windowMs` of its last ACCEPTED read — a QR
 * held in front of the camera is decoded many times per second. A different
 * token is always accepted.
 */
export function createScanDebouncer(windowMs: number = SCAN_DEBOUNCE_MS) {
    const lastAccepted = new Map<string, number>();
    return {
        accept(token: string, nowMs: number): boolean {
            const previous = lastAccepted.get(token);
            if (previous !== undefined && nowMs - previous < windowMs) return false;
            lastAccepted.set(token, nowMs);
            // Bounded: only the recent past matters.
            for (const [key, at] of lastAccepted) {
                if (nowMs - at >= windowMs) lastAccepted.delete(key);
            }
            return true;
        },
        reset() {
            lastAccepted.clear();
        },
    };
}

// ---------------------------------------------------------- repeat guard ----

/** How long the badge of the last result must be OUT OF VIEW before it counts again. */
export const SAME_BADGE_QUIET_MS = 15_000;

/**
 * After a result, the agent often keeps the phone on the badge while reading
 * the screen. The debouncer alone re-accepted it as soon as the overlay closed,
 * which recorded a second entry and flipped the screen to « déjà entré » for
 * someone who had just come in. This guard ignores the last result's token
 * while it keeps being seen: every sighting restarts the quiet window, so it
 * only counts again once it has left the camera for `quietMs`, or after an
 * explicit « Scan suivant » (clear()).
 */
export function createRepeatGuard(quietMs: number = SAME_BADGE_QUIET_MS) {
    let last: { token: string; seenAt: number } | null = null;
    return {
        /** True when this sighting is the last result's badge still in view. */
        ignore(token: string, nowMs: number): boolean {
            if (!last || last.token !== token) return false;
            if (nowMs - last.seenAt >= quietMs) {
                last = null;
                return false;
            }
            last.seenAt = nowMs;
            return true;
        },
        /** Record the token whose result is now on screen. */
        mark(token: string, nowMs: number) {
            last = { token, seenAt: nowMs };
        },
        clear() {
            last = null;
        },
    };
}

// --------------------------------------------------------------- outcome ----

/**
 * granted — entry allowed;
 * warning — allowed, but the agent must look (already entered, scan not
 *           recorded as an entry, badge not for today…);
 * refused — the server said no;
 * error   — no decision (network, rate limit, server error).
 */
export type ScanOutcomeStatus = "granted" | "warning" | "refused" | "error";

export type ScanOutcomeWarning =
    /** previous_entry: this accreditation already entered today. */
    | "already_entered"
    /** The server did not give the scanner tier (session lost / role missing): nothing recorded. */
    | "not_recorded"
    /** Valid, but not on the match day (timing). */
    | "off_day";

export interface ScanOutcomeHolder {
    name: string;
    nameAr: string | null;
    organization: string;
    photoUrl: string | null;
    mediaType: string | null;
    zones: string[];
    qrKind: ScanQrKind | null;
}

export interface ScanOutcome {
    id: string;
    token: string;
    /** Epoch ms of the client-side decision. */
    at: number;
    status: ScanOutcomeStatus;
    /** `media` namespace key of the refusal / error reason, null when granted. */
    reasonKey: string | null;
    warnings: ScanOutcomeWarning[];
    previousEntryAt: string | null;
    holder: ScanOutcomeHolder | null;
    /** The badge's match (v2), when the server sent it. */
    match: ScanMatchSummary | null;
    /** The badge is for another match than the one being controlled. */
    wrongMatch: boolean;
    timing: ScanTiming | null;
    /** Accreditation id (full tier) — dedupes the session entry counter. */
    accreditationId: string | null;
}

let outcomeSeq = 0;
function nextOutcomeId(at: number): string {
    outcomeSeq += 1;
    return `${at}-${outcomeSeq}`;
}

function holderOf(response: ScanAccreditationResponse): ScanOutcomeHolder | null {
    const acc = response.accreditation;
    if (!acc) return null;
    return {
        name: acc.holder_name,
        nameAr: acc.holder_name_ar?.trim() || null,
        organization: acc.organization,
        photoUrl: acc.photo_url ?? acc.holder_photo ?? null,
        mediaType: acc.media_type ?? null,
        zones: [...(acc.approved_zones ?? [])],
        qrKind: acc.qr_kind ?? null,
    };
}

/**
 * Turns a scan-accreditation response into what the gate screen shows.
 * `controlledMatchId` is the match the agent picked.
 */
export function classifyScanResponse(
    token: string,
    response: ScanAccreditationResponse,
    controlledMatchId: string | null,
    at: number
): ScanOutcome {
    const holder = holderOf(response);
    const acc = response.accreditation;
    const badgeMatchId = response.match?.id ?? acc?.match_id ?? null;
    const wrongMatch =
        response.error === "INVALID_MATCH" ||
        (!!controlledMatchId && !!badgeMatchId && badgeMatchId !== controlledMatchId);
    const base = {
        id: nextOutcomeId(at),
        token,
        at,
        holder,
        match: response.match ?? null,
        wrongMatch,
        timing: response.timing ?? null,
        accreditationId: acc?.id ?? null,
        previousEntryAt: response.previous_entry?.scanned_at ?? null,
    };

    if (!response.valid || wrongMatch) {
        return {
            ...base,
            status: "refused",
            reasonKey: wrongMatch && response.valid
                ? "scan.reasons.wrong_match"
                : verifyErrorKey(response.error),
            warnings: [],
        };
    }

    const warnings: ScanOutcomeWarning[] = [];
    if (response.previous_entry?.scanned_at) warnings.push("already_entered");
    // v2 says it outright; v1 only gives the full tier (with an id) to a
    // recognised scanner, and only those scans are entries.
    const recordedAsEntry = response.recorded_as
        ? response.recorded_as === "entry"
        : !!acc?.id;
    if (!recordedAsEntry) warnings.push("not_recorded");
    if (response.timing && response.timing !== "match_day") warnings.push("off_day");

    return {
        ...base,
        status: warnings.length > 0 ? "warning" : "granted",
        reasonKey: null,
        warnings,
    };
}

/** A thrown service error (HTTP status, rate limit, network) — no decision. */
export function classifyScanError(token: string, message: string | null | undefined, at: number): ScanOutcome {
    return {
        id: nextOutcomeId(at),
        token,
        at,
        status: "error",
        reasonKey: verifyErrorKey(message),
        warnings: [],
        previousEntryAt: null,
        holder: null,
        match: null,
        wrongMatch: false,
        timing: null,
        accreditationId: null,
    };
}

/** Allowed in (granted, or allowed with a warning). */
export function isAllowedOutcome(outcome: Pick<ScanOutcome, "status">): boolean {
    return outcome.status === "granted" || outcome.status === "warning";
}

/**
 * Distinct people let in during this session: allowed outcomes, a re-entry
 * (already_entered) or an unrecorded scan does not add one; deduped by
 * accreditation id, else by token.
 */
export function sessionEntryCount(outcomes: readonly ScanOutcome[]): number {
    const seen = new Set<string>();
    for (const o of outcomes) {
        if (!isAllowedOutcome(o)) continue;
        if (o.warnings.includes("already_entered") || o.warnings.includes("not_recorded")) continue;
        seen.add(o.accreditationId ?? `token:${o.token}`);
    }
    return seen.size;
}

export const RECENT_SCANS_LIMIT = 10;

/** Newest first, capped. */
export function pushRecent(
    list: readonly ScanOutcome[],
    outcome: ScanOutcome,
    limit: number = RECENT_SCANS_LIMIT
): ScanOutcome[] {
    return [outcome, ...list].slice(0, limit);
}

/** Holder name in ONE language: the Arabic name in Arabic mode when the server sent it. */
export function localizedHolderName(
    holder: { name?: string | null; nameAr?: string | null; holder_name?: string | null; holder_name_ar?: string | null } | null | undefined,
    displayMode: DisplayMode
): string | null {
    if (!holder) return null;
    const latin = (holder.name ?? holder.holder_name ?? "").trim();
    const arabic = (holder.nameAr ?? holder.holder_name_ar ?? "").trim();
    if (displayMode === "arabic" && arabic) return arabic;
    return latin || arabic || null;
}

// -------------------------------------------------------------- feedback ----

export interface ScanFeedback {
    /** navigator.vibrate pattern (ms). */
    vibrate: number[];
    /** Successive tones: frequency (Hz) and duration (ms). */
    tones: { freq: number; ms: number; type: "sine" | "square" | "triangle" }[];
}

export function outcomeFeedback(status: ScanOutcomeStatus): ScanFeedback {
    switch (status) {
        case "granted":
            return {
                vibrate: [90],
                tones: [
                    { freq: 880, ms: 90, type: "sine" },
                    { freq: 1320, ms: 140, type: "sine" },
                ],
            };
        case "warning":
            return {
                vibrate: [120, 80, 120, 80, 120],
                tones: [
                    { freq: 660, ms: 120, type: "triangle" },
                    { freq: 660, ms: 120, type: "triangle" },
                ],
            };
        case "refused":
            return {
                vibrate: [300, 120, 300],
                tones: [{ freq: 196, ms: 450, type: "square" }],
            };
        default:
            return {
                vibrate: [200],
                tones: [{ freq: 330, ms: 250, type: "triangle" }],
            };
    }
}

// --------------------------------------------------------- match picking ----

export interface ScanModeMatch {
    id: string;
    match_date: string;
    match_time?: string | null;
    status?: string | null;
    game_day?: number | null;
    home_team?: { name: string; name_ar?: string | null } | null;
    away_team?: { name: string; name_ar?: string | null } | null;
    stadium?: { name: string; name_ar?: string | null } | null;
}

export interface ScanModeMatchList<M extends ScanModeMatch> {
    /** What the picker shows first. */
    matches: (M & { approvedCount: number })[];
    /** All of today's matches (for « show all »). */
    all: (M & { approvedCount: number })[];
    /** True when no match has an approved accreditation today, so `matches` is every match. */
    fallback: boolean;
}

const DEAD_STATUSES = new Set(["cancelled", "postponed"]);

/**
 * Today's matches for the picker: those with approved accreditations, else all
 * of them (a security_gate cannot read accreditation requests — RLS — so its
 * counts are always empty and it gets the full list). Cancelled / postponed
 * matches are left out; sorted by kick-off, matches without a time last.
 */
export function scanModeMatchList<M extends ScanModeMatch>(
    matches: readonly M[],
    approvedCounts: ReadonlyMap<string, number>
): ScanModeMatchList<M> {
    const all = matches
        .filter((m) => !DEAD_STATUSES.has(String(m.status ?? "")))
        .map((m) => ({ ...m, approvedCount: approvedCounts.get(m.id) ?? 0 }))
        .sort(
            (a, b) =>
                (a.match_time ?? "99").localeCompare(b.match_time ?? "99") ||
                a.id.localeCompare(b.id)
        );
    const accredited = all.filter((m) => m.approvedCount > 0);
    return accredited.length > 0
        ? { matches: accredited, all, fallback: false }
        : { matches: all, all, fallback: true };
}

/** sessionStorage key of the picked match (per league and Algiers day). */
export function scanModeMatchStorageKey(leagueId: string | null, dayKey: string): string {
    return `media-scan-mode:match:${leagueId ?? "none"}:${dayKey}`;
}

export const SCAN_DEVICE_ID_KEY = "media-scan-mode:device-id";

/** Stable per-browser device id (localStorage), generated once. */
export function getScanDeviceId(
    storage: Pick<Storage, "getItem" | "setItem"> | null | undefined,
    generate: () => string
): string {
    const existing = storage?.getItem(SCAN_DEVICE_ID_KEY);
    if (existing) return existing;
    const id = `webapp-${generate()}`;
    try {
        storage?.setItem(SCAN_DEVICE_ID_KEY, id);
    } catch {
        // Private mode / quota: an id per page load is still better than none.
    }
    return id;
}

// ------------------------------------------------- /media-verify context ----

/**
 * valid           — green, as before (match day, or a v1 server);
 * before_match    — neutral: valid badge, but the gate opens on the match day;
 * not_yet_valid   — neutral: refused only because validity has not started;
 * match_over      — grey: the match day is past;
 * invalid         — red.
 */
export type VerifyCardState =
    | "valid"
    | "before_match"
    | "not_yet_valid"
    | "match_over"
    | "invalid";

const EXPIRY_CODES = new Set(["TOKEN_EXPIRED", "ACCREDITATION_EXPIRED"]);

export function verifyCardState(response: ScanAccreditationResponse): VerifyCardState {
    const code = (response.error ?? "").toUpperCase();
    if (!response.valid) {
        if (code === "ACCREDITATION_NOT_YET_VALID") return "not_yet_valid";
        if (EXPIRY_CODES.has(code) && response.timing === "after_match") return "match_over";
        return "invalid";
    }
    if (response.timing === "before_match_day") return "before_match";
    if (response.timing === "after_match") return "match_over";
    return "valid";
}

// ------------------------------------------------------- match summary ----

type SummaryName = { name: string | null; name_ar: string | null } | null | undefined;

function named(n: SummaryName): { name: string; name_ar: string | null } | null {
    const name = n?.name?.trim() || n?.name_ar?.trim() || "";
    return name ? { name, name_ar: n?.name_ar?.trim() || null } : null;
}

/**
 * The v2 `match` summary in the shape of the board / cockpit match labels
 * (`home_team` / `away_team` / `stadium` with `name` + `name_ar`), so one set of
 * displayMode-aware formatters serves both.
 */
export function summaryToMatchInfo(m: ScanMatchSummary) {
    return {
        id: m.id,
        match_date: m.date ?? "",
        match_time: m.time ?? null,
        home_team: named(m.home),
        away_team: named(m.away),
        stadium: named(m.stadium),
    };
}
